import type { CSSProperties } from "react";
import { cx } from "../control/ui/primitives";

/**
 * "Join the Discord" — the one place this link is built, wherever it appears.
 *
 * WHY IT LOOKS LIKE DISCORD AND NOT LIKE US. Every other button on this site is the
 * site's accent yellow. This one deliberately breaks that, because it is the only
 * control that hands somebody to a different product: the logo and the blurple ARE the
 * label, recognised before the words are read, and a yellow button reading "Join the
 * Discord" asks people to trust a claim instead of a mark they already know. Same
 * reasoning every "Sign in with Google" button in existence is Google-coloured.
 *
 * The invite is a CONSTANT here rather than a setting. It is technically a rotating
 * credential — revoke it, or regenerate it after a raid, and this needs a deploy — but
 * a settings field plus a panel input plus a boundary validator is a lot of machinery
 * for a string that changes approximately never. If it starts changing, the shape to
 * move to is `gold.paymentUrl`'s: a field in SystemSettings, sanitized in admin.ts.
 */
const INVITE = "https://discord.gg/7UFf9K5YH";

/** Discord's own brand colour ("Blurple", #5865F2) and its hover shade, from their brand
 *  guidelines. Hardcoded rather than tokenised: these are THEIR colours, and a theme
 *  change here would make the button stop being recognisable, which is its whole job. */
const BLURPLE = "#5865F2";
const BLURPLE_HOVER = "#4752C4";

/** The Discord mark. `currentColor` so one path serves both treatments — white on the
 *  blurple plate, blurple beside white type — without a second copy. */
export function DiscordMark({
  size = 20,
  className,
  style,
}: {
  size?: number;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
      className={className}
      style={style}
    >
      <path d="M20.317 4.3697a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
    </svg>
  );
}

/**
 * The link.
 *
 * `solid` is the full blurple plate for a page that is ASKING (the thanks screens, where
 * it is the only thing left to do); `quiet` is a white label beside a blurple mark, for
 * chrome that is merely offering (the remote, where it sits under the controls and must
 * not compete with them).
 *
 * `target="_blank"` with `rel="noreferrer"`: on the remote especially, this must not
 * navigate the tab away — that tab is a live control surface holding a paired screen,
 * and losing it to leave the site would cost the session.
 */
export function DiscordLink({
  variant = "solid",
  label = "Join the Discord",
  className,
}: {
  variant?: "solid" | "quiet";
  label?: string;
  className?: string;
}) {
  const solid = variant === "solid";
  return (
    <a
      href={INVITE}
      target="_blank"
      rel="noreferrer"
      style={solid ? { backgroundColor: BLURPLE } : undefined}
      onMouseEnter={(e) => {
        if (solid) e.currentTarget.style.backgroundColor = BLURPLE_HOVER;
      }}
      onMouseLeave={(e) => {
        if (solid) e.currentTarget.style.backgroundColor = BLURPLE;
      }}
      className={cx(
        "inline-flex items-center justify-center gap-2 rounded-lg text-sm font-semibold transition-colors",
        solid ? "w-full px-4 py-2.5 text-white" : "px-3 py-2 text-white/90 hover:bg-white/5",
        className,
      )}
    >
      {/* The MARK carries the blue in the quiet variant and the plate carries it in the
          solid one, but the label is white in both. Blurple text on this near-black is
          legible in a bright room and murky in a dark one — and a dark room is the
          remote's whole habitat. White type, blue mark: Discord's own treatment for
          dark UI, and still their two colours. */}
      <DiscordMark size={solid ? 20 : 18} style={solid ? undefined : { color: BLURPLE }} />
      {label}
    </a>
  );
}
